import { Badge } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import SidebarLink from "../../layouts/sidebar/SidebarLink";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { useConfirmations } from "./api/useConfirmations";
import { RiRefreshLine } from "react-icons/ri";
import { FaCcAmazonPay } from "react-icons/fa";
import { MdManageSearch } from "react-icons/md";
import { MdOutlineTimeline } from "react-icons/md";
import { useAppSelector } from "../../redux/hooks";
import { useFetch } from "../../hooks/api";
import { useSimpleCan } from "../../config/casl/casl";

const ConfirmationCountBadge = ({ marketPlace, date }) => {
  const { data: confirmations = [], isLoading } = useConfirmations({
    marketPlace: marketPlace.marketPlace,
    date: moment(date).format("YYYY-MM-DD"),
    limit: 100,
  });

  const confirmationCnt = confirmations.length;

  return (
    <Badge className={`badge badge-secondary`} pill style={{ marginLeft: "1rem" }}>
      {isLoading ?
        "..."
      : confirmationCnt >= 20 ?
        "20+"
      : confirmationCnt}
    </Badge>
  );
};

const PricingSidebar = ({ small = false }) => {
  const { t } = useTranslation(["pricingv2"]);
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const hasForecastModule = useModuleEnabled("FORECAST");
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  const isActive = (routeName) => {
    return document.location.pathname.indexOf(routeName) > -1 ? "active" : "";
  };

  const buyBoxStatsQuery = useFetch(
    ["buyboxStats", marketPlace.marketPlace, user?.organization_id],
    `/pricing/buybox/stats`,
    {
      market_place: marketPlace.marketPlace,
    },

    {
      default: [],
      select: (data) => data?.data?.data?.length,
    },
  );

  const now = moment();
  const tosDate = moment(user?.tos_acceptance_date?.slice(0, 10));
  const daysSinceSignup = tosDate ? now.diff(tosDate, "days") : 100;

  const endpoint = "/user/pricing/dashboard";

  return (
    <>
      <SidebarLink
        isActive={isActive("/user/pricing/dashboard")}
        to={`${endpoint}`}
        icon={<i className="pe-7s-cash" />}
        small={small}
        section={"pricing"}
      >
        {t(($) => $.pricingv2["Dynamic Pricing"])}{" "}
        {user.tier === "unknown" && user.pricing_tier === "unknown" && daysSinceSignup < 3 && (
          <Badge
            className={`badge badge-${isActive(endpoint) ? "secondary" : "primary"}`}
            pill
            style={{ marginLeft: "1rem" }}
          >
            {t(($) => $.pricingv2["NEW"])}
          </Badge>
        )}
      </SidebarLink>
      <SidebarLink
        isActive={isActive(`/user/pricing/forecast`)}
        to={`/user/pricing/forecast`}
        icon={
          <span className="fs-2">
            <MdOutlineTimeline style={{ color: "#2e0054" }} />
          </span>
        }
        small={small}
        section={"pricing"}
        isVisible={hasForecastModule}
      >
        {t(($) => $.pricingv2["Forecast Dashboard"])}
      </SidebarLink>
      <SidebarLink
        isActive={isActive(`/user/pricing/management`) || isActive("/user/pricing/plan")}
        to={`/user/pricing/management`}
        icon={
          <span className="fs-2">
            <MdManageSearch style={{ color: "#2e0054" }} />
          </span>
        }
        small={small}
        section={"pricing"}
      >
        {t(($) => $.pricingv2["Management"])}
      </SidebarLink>
      {userCanUpdatePricingPlans && (
        <SidebarLink
          isActive={isActive(`/user/pricing/configuration`)}
          to={`/user/pricing/configuration`}
          icon={<i className="fa fa-cog" />}
          small={small}
          section={"pricing"}
        >
          {t(($) => $.pricingv2["Configuration"])}
        </SidebarLink>
      )}
      <SidebarLink
        isActive={isActive(`/user/pricing/changes`)}
        to={`/user/pricing/changes`}
        icon={
          <span className="fs-3">
            <RiRefreshLine style={{ color: "#2e0054" }} />
          </span>
        }
        small={small}
        section={"pricing"}
      >
        {t(($) => $.pricingv2["Price Logs"])}
        <ConfirmationCountBadge marketPlace={marketPlace} date={now} />
      </SidebarLink>

      <SidebarLink
        isActive={isActive(`/user/pricing/buybox`)}
        to={`/user/pricing/buybox`}
        icon={
          <span className="fs-3">
            <FaCcAmazonPay style={{ color: "#2e0054" }} />
          </span>
        }
        small={small}
        section={"pricing"}
        isVisible={channel === "amazon"}
      >
        {t(($) => $.pricingv2["Buy Box Health"])}
        <Badge className={`badge badge-secondary`} pill style={{ marginLeft: "1rem" }}>
          {buyBoxStatsQuery.isLoading ?
            "..."
          : buyBoxStatsQuery.data >= 20 ?
            "20+"
          : buyBoxStatsQuery.data}
        </Badge>
      </SidebarLink>
    </>
  );
};

export default PricingSidebar;
